<template>
  <!-- PAGE TRANSITION -->
  <transition name="page-fade" appear>
    <div v-if="!pageLoading">

      <!-- HERO -->
      <PageHero
        :title="$t('hero.title_agencies')"
        :breadcrumbsTop="[
          { label: $t('hero.breadcrumbs.home'), link: '/' },
          { label: $t('hero.breadcrumbs.agencies') }
        ]"
        :backgroundImageUrl="heroBackground"
        :loaded="true"
      />

      <!-- CONTENT -->
      <div class="container py-10">

        <MiniFilterBar />

        <div class="row mt-4">
          <div
            v-for="agency in agencies"
            :key="agency.id"
            class="col-md-4 mb-4"
          >
            <div class="card border-0 shadow-sm h-100 rounded-5 overflow-hidden">
              <div class="card-body d-flex flex-column">

                <h3 class="h5 mb-2 card-title">
                  <router-link
                    :to="`/public/agencies/${agency.id}`"
                    class="text-decoration-none text-dark"
                  >
                    {{ agency.name }}
                  </router-link>
                </h3>

                <ul class="list-inline text-muted small mb-3">
                  <li v-if="agency.agent_name" class="list-inline-item">
                    👤 {{ agency.agent_name }}
                  </li>
                  <li v-if="agency.telegram" class="list-inline-item">
                    💬 Telegram: {{ agency.telegram }}
                  </li>
                  <li v-if="agency.phone" class="list-inline-item">
                    📞 {{ agency.phone }}
                  </li>
                  <li class="list-inline-item">
                    👥 Агентов: {{ agency.agent_count }}
                  </li>
                  <li class="list-inline-item">
                    🏠 Объектов: {{ agency.property_count }}
                  </li>
                </ul>

                <router-link
                  :to="`/public/agencies/${agency.id}`"
                  class="mt-auto btn secondary-solid-btn btn-sm"
                >
                  Подробнее
                </router-link>

              </div>
            </div>
          </div>
        </div>

        <!-- EMPTY -->
        <div v-if="agencies.length === 0" class="text-center text-muted py-5">
          {{ $t('agency.no_available') || 'Нет доступных агентств' }}
        </div>

      </div>
    </div>
  </transition>

  <!-- SKELETON -->
  <div v-if="pageLoading" class="page-skeleton">
    <div class="skeleton hero-skeleton"></div>
    <div class="container mt-4">
      <div class="row">
        <div v-for="i in 6" :key="i" class="col-md-4 mb-4">
          <div class="skeleton card-skeleton"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useLanguageStore } from '@/stores/languageStore'

import PageHero from '../components/PageHero.vue'
import MiniFilterBar from '../components/MiniFilterBar.vue'

/* PAGE STATE */
const pageLoading = ref(true)

/* LANGUAGE */
const { locale } = storeToRefs(useLanguageStore())

/* HERO */
const heroBackground = ref(null)

/* AGENCIES */
const agencies = ref([])

/* LOAD HERO */
async function loadHeroBanner () {
  const res = await fetch(
    `/api/v1/banners?placement=agencies`,
    {
      headers: { 'X-Locale': (locale.value || 'ru').split('-')[0] },
      cache: 'no-store'
    }
  )

  const banner = (await res.json())?.[0]
  if (!banner?.image_url) return

  const img = new Image()
  img.src = banner.image_url
  await img.decode()

  heroBackground.value = banner.image_url
}

/* LOAD AGENCIES */
async function loadAgencies () {
  const res = await fetch('/api/v1/agencies', { cache: 'no-store' })
  agencies.value = await res.json()
}

/* RELOAD PAGE */
async function reloadPage () {
  pageLoading.value = true
  await Promise.all([
    loadHeroBanner(),
    loadAgencies()
  ])
  pageLoading.value = false
}

/* INIT */
onMounted(reloadPage)
watch(locale, reloadPage)

</script>


<style scoped>
/* PAGE TRANSITION */
.page-fade-enter-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}
.page-fade-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.page-fade-enter-to {
  opacity: 1;
  transform: translateY(0);
}

/* CARD HOVER */
.card:hover {
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.15);
  transition: box-shadow 0.3s ease;
}

/* SKELETON */
.skeleton {
  background: linear-gradient(90deg, #eee, #f5f5f5, #eee);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite;
  border-radius: 12px;
}
.hero-skeleton {
  height: 516px;
}
.card-skeleton {
  height: 220px;
}
@keyframes shimmer {
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
}

</style>
